import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useCheckForMatchesMutation } from "api";
import { useDispatch, useSelector } from "react-redux";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { RootState } from "store";
import { Input, Button, FormGroup, Label } from "@mwi/ui";
import useErrorHandler from "hooks/useErrorHandler";
import useTitle from "hooks/useTitle";
import LoginSideImage from "assets/images/login-side.jpg";
import { setEmail } from "slices/auth";

type Inputs = {
  email: string;
};

const StartPage = () => {
  useTitle("Welcome");

  const dispatch = useDispatch();
  const location = useLocation();
  const errorHandler = useErrorHandler();
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    setError,
    getValues,
    formState: { errors },
  } = useForm<Inputs>();

  const [checkMatches, { data: matchResult, isLoading, error }] =
    useCheckForMatchesMutation();

  const { isAuthenticated, user } = useSelector((state: RootState) => ({
    isAuthenticated: state.auth.authenticated,
    user: state.auth.user,
  }));

  useEffect(() => {
    if (error) {
      errorHandler(error, setError);
    }
  }, [error]);

  useEffect(() => {
    if (matchResult) {
      dispatch(setEmail(getValues("email")));
      if (matchResult.flow === "login") {
        navigate("/login");
      } else {
        // TODO: Handle error here
      }
    }
  }, [dispatch, matchResult]);

  if (isAuthenticated && user?.onboarding_completed_at) {
    const to = (location.state as any)?.from?.pathname ?? "/admin";
    return <Navigate to={to} replace />;
  }

  return (
    <form
      onSubmit={handleSubmit(checkMatches)}
      className="flex flex-col h-full pt-10 tablet:pt-1"
      noValidate
    >
      <div className="hidden desktop:block fixed inset-y-0 left-0 right-[70%]">
        <img
          src={LoginSideImage}
          alt=""
          className="object-cover w-full h-full"
        />
      </div>

      <div className="desktop:fixed inset-y-0 right-0 left-[30%] flex-grow">
        <div className="flex flex-col justify-between w-full h-full mx-auto max-w-[37.5rem]">
          <div className="flex flex-col justify-center h-full tablet:-mt-16">
            <h1 className="mt-16 text-4xl font-bold leading-10 tablet:text-6xl">
              Let&apos;s get started
            </h1>

            <p className="hidden mt-8 text-lg tablet:block">
              Enter your email address
            </p>

            <FormGroup
              className="!mt-10 tablet:!mt-14"
              error={errors.email?.message}
            >
              <Label>Email Address</Label>
              <Input type="email" {...register("email")} />
            </FormGroup>

            <div className="pt-8 mt-auto tablet:mt-0">
              <Button
                className="!px-[60px] !py-[18px] w-full tablet:w-max"
                type="submit"
                size="large"
                loading={isLoading}
              >
                Continue
              </Button>
            </div>
          </div>
        </div>
      </div>
    </form>
  );
};

export default StartPage;
